import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchArticlesByScope,
    setCachedScope,
    setCurrentScope,
    scopeToKey,
    getParentScopeKey,
    isScopeStale,
} from '../slices/unifiedArticlesSlice.mjs';

/**
 * Filter articles from a parent scope to match a child scope.
 * 
 * @param {Array} articleGroups - The article groups from the parent scope
 * @param {Object} childScope - The scope to filter for
 * @returns {Array} - Filtered article groups
 */
const filterArticlesForScope = (articleGroups, childScope) => {
    if (!Array.isArray(articleGroups) || articleGroups.length === 0) {
        return [];
    }

    // For topic filtering from category data
    if (childScope.topic) {
        // Filter to articles matching the topic
        return articleGroups.map(group => ({
            ...group,
            articles: group.articles?.filter(article => {
                // Check if article belongs to this topic
                const articleTopics = article.topics || [];
                const articleTags = article.tags || [];

                // Match by topic code/slug
                return (
                    articleTopics.includes(childScope.topic) ||
                    articleTags.includes(childScope.topic)
                );
            }) || [],
        })).filter(group => group.articles.length > 0);
    }

    // For category filtering from home data
    if (childScope.category && childScope.type === 'category') {
        return articleGroups.filter(group =>
            group.category === childScope.category
        );
    }

    return articleGroups;
};

/**
 * Custom hook for scope-aware article access.
 * 
 * Features:
 * - Automatic scope tracking and caching
 * - Smart filtering from parent scopes when available
 * - Stale data detection and refresh
 * - SSR hydration support
 * 
 * @param {Object} requestedScope - The scope to fetch articles for
 * @param {Object} options - Additional options
 * @param {boolean} options.skipFetch - Skip automatic fetching (for SSR hydration)
 * @param {boolean} options.forceRefresh - Force a fresh fetch even if cached
 * @returns {Object} - { articles, isLoading, error, scopeKey, refetch }
 */
export const useArticleScope = (requestedScope, options = {}) => {
    const { skipFetch = false, forceRefresh = false } = options;
    const dispatch = useDispatch();

    // SSR hydration guard: on the very first effect run, if data already exists
    // in the store (from preloadedState), skip fetching. This prevents a re-fetch
    // during hydration that could return different data (the client API for 'home'
    // scope uses a different service than the SSR controller).
    const hasHydrated = useRef(false);

    // Select unified articles state
    const {
        articlesByScope,
        fetchedScopes,
        currentScope,
        isLoading,
        error,
        pagination,
        currentTopicMetadata,
    } = useSelector(state => state.unifiedArticles);

    // Compute scope key
    const scopeKey = useMemo(() => scopeToKey(requestedScope), [requestedScope]);
    const parentScopeKey = useMemo(() => getParentScopeKey(scopeKey), [scopeKey]);

    // Check if we have valid data for this scope
    const fetchStatus = fetchedScopes[scopeKey];
    const hasValidData = fetchStatus?.success && !isScopeStale(fetchStatus);
    const isPending = fetchStatus?.pending;
    // Circuit-breaker: treat a failed fetch as terminal (don't auto-retry on error)
    const hasError = fetchStatus?.fetched && fetchStatus?.success === false;

    // Check if we can filter from a parent scope
    const parentFetchStatus = parentScopeKey ? fetchedScopes[parentScopeKey] : null;
    const canFilterFromParent = parentFetchStatus?.success && !isScopeStale(parentFetchStatus);

    // Update current scope when this hook mounts or scope changes.
    // NOTE: depend on scopeKey (primitive string) instead of requestedScope
    // (object recreated each render) to avoid infinite dispatch loops.
    useEffect(() => {
        const currentScopeKey = scopeToKey(currentScope);
        if (currentScopeKey !== scopeKey) {
            dispatch(setCurrentScope(requestedScope));
        }
    }, [scopeKey, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch or filter data as needed
    useEffect(() => {
        // Skip if explicitly requested
        if (skipFetch) {
            return;
        }

        // SSR hydration guard: on the very first effect run, if data already
        // exists in the store (from preloadedState), trust it and skip fetching.
        // This prevents re-fetching during hydration which can return different
        // data (e.g., home scope uses getHomeFeaturedArticlesService on SSR but
        // getStreamlinedSourceArticlesService on the client API).
        if (!hasHydrated.current) {
            hasHydrated.current = true;
            if (fetchStatus?.success) {
                return;
            }
        }

        // Skip if already fetching
        if (isPending) {
            return;
        }

        // If we have valid data and not forcing refresh, skip
        if (hasValidData && !forceRefresh) {
            return;
        }

        // Circuit-breaker: don't auto-retry on error (user can manually refetch)
        if (hasError && !forceRefresh) {
            return;
        }

        // If we can filter from parent scope, do that instead of fetching
        if (canFilterFromParent && !forceRefresh) {
            const parentArticles = articlesByScope[parentScopeKey];
            const filtered = filterArticlesForScope(parentArticles, requestedScope);

            // Only use filtered data if we got results
            if (filtered.length > 0) {
                dispatch(setCachedScope({
                    scopeKey,
                    articles: filtered,
                    pagination: null, // Filtering doesn't preserve pagination
                    topicMetadata: currentTopicMetadata,
                }));
                return;
            }
            // Fall through to fetch if filtering returned empty
        }

        // Fetch fresh data
        dispatch(fetchArticlesByScope({
            ...requestedScope,
            page: requestedScope.page || 1,
            limit: requestedScope.limit || pagination?.limit || 25,
        }));
    }, [
        scopeKey,
        skipFetch,
        forceRefresh,
        hasValidData,
        hasError,
        isPending,
        canFilterFromParent,
        dispatch,
    ]);

    // Get articles for current scope
    const articles = useMemo(() => {
        return articlesByScope[scopeKey] || [];
    }, [articlesByScope, scopeKey]);

    // Refetch function for manual refresh
    const refetch = () => {
        dispatch(fetchArticlesByScope({
            ...requestedScope,
            page: pagination?.page || 1,
            limit: pagination?.limit || 25,
        }));
    };

    return {
        articles,
        isLoading: isLoading || isPending,
        error,
        scopeKey,
        pagination,
        topicMetadata: currentTopicMetadata,
        hasValidData,
        refetch,
    };
};

/**
 * Helper hook to build a scope object from route params.
 * 
 * @param {string} categorySlug - Category from route params
 * @param {string[]} topicSegments - Topic path segments from route params (optional)
 * @returns {Object} - Scope object
 */
export const buildScopeFromParams = (categorySlug, topicSegments = []) => {
    if (!categorySlug) {
        return { type: 'home' };
    }

    if (topicSegments.length === 0) {
        return { type: 'category', category: categorySlug };
    }

    const topicSlug = topicSegments[topicSegments.length - 1];
    const ancestors = topicSegments.slice(0, -1);

    return {
        type: 'topic',
        category: categorySlug,
        ...(ancestors.length ? { ancestors } : {}),
        topic: topicSlug,
    };
};

export default useArticleScope;
